import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ParagraphPickerOptions } from '@/modules/documents/components/HouseholdDocumentsViewer/FileParagraphPicker';
import { AnswerReviewEvidence, AnswerReviewFieldViewModel, AnswerReviewWorkbenchState } from '../AnswerReviewWorkbench.types';
import { flattenReviewFields } from '../recursiveSubmissionAnswers';
type CitationAction = 'add' | 'remove';
const NO_ACTIVE_ANNOTATION = -1;
export function useFormSubmissionCitations(t0) {
  const $ = _c(57);
  const {
    sources,
    state,
    canAdd,
    addCitation,
    removeCitation
  } = t0;
  const [selectedDocumentId, setSelectedDocumentId] = useState();
  const [selectedEvidence, setSelectedEvidence] = useState();
  const [activeAnnotationIndex, setActiveAnnotationIndex] = useState();
  const [focusedFieldId, setFocusedFieldId] = useState();
  const [action, setAction] = useState();
  const [focusRequest, setFocusRequest] = useState(0);
  const [focusKeepsQuery, setFocusKeepsQuery] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const beforeAction = useRef(t1);
  let t2;
  if ($[1] !== action) {
    t2 = () => {
      setAction(undefined);
      if (action === "remove") {
        return;
      }
      setSelectedEvidence(beforeAction.current.evidence);
      setSelectedDocumentId(beforeAction.current.documentId);
      setActiveAnnotationIndex(NO_ACTIVE_ANNOTATION);
    };
    $[1] = action;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const cancel = t2;
  let t3;
  if ($[3] !== state.kind || $[4] !== state.review) {
    t3 = state.kind === "ready" ? flattenReviewFields(state.review.sections.flatMap(_temp)) : [];
    $[3] = state.kind;
    $[4] = state.review;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const fields = t3;
  let t4;
  if ($[6] !== focusedFieldId) {
    t4 = field => field.id === focusedFieldId;
    $[6] = focusedFieldId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const focusedField = fields.find(t4);
  const activeDocumentId = selectedDocumentId ?? sources[0]?.id;
  let t5;
  let t6;
  if ($[8] !== action || $[9] !== cancel || $[10] !== focusedFieldId) {
    t5 = () => {
      if (!focusedFieldId) {
        return;
      }
      let menuHandledEscape = false;
      const handleEscape = event => {
        if (event.key !== "Escape") {
          return;
        }
        if (event.type === "keydown") {
          menuHandledEscape = event.target instanceof Element && !!event.target.closest("[role=\"menu\"], [role=\"listbox\"], [role=\"search\"]");
          if (menuHandledEscape) {
            return;
          }
        }
        event.preventDefault();
        event.stopPropagation();
        if (event.type !== "keyup") {
          return;
        }
        if (menuHandledEscape) {
          menuHandledEscape = false;
          return;
        }
        if (action) {
          cancel();
        } else {
          setFocusedFieldId(undefined);
          setSelectedEvidence(undefined);
        }
      };
      window.addEventListener("keydown", handleEscape, true);
      window.addEventListener("keyup", handleEscape, true);
      return () => {
        window.removeEventListener("keydown", handleEscape, true);
        window.removeEventListener("keyup", handleEscape, true);
      };
    };
    t6 = [action, focusedFieldId, cancel];
    $[8] = action;
    $[9] = cancel;
    $[10] = focusedFieldId;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  const removeParagraphIds = focusedField?.evidence.flatMap(_temp2) ?? [];
  const picker = action && focusedField && canAdd ? {
    action,
    excludedParagraphIds: action === "add" ? focusedField.evidence.flatMap(_temp3) : [],
    eligibleParagraphIds: action === "remove" ? removeParagraphIds : undefined,
    activeParagraphIds: selectedEvidence?.paragraphIds,
    onCancel: cancel,
    onSelect: (paragraph, document) => {
      if (document.id !== activeDocumentId) {
        return;
      }
      if (action === "remove") {
        const evidence_1 = focusedField.evidence.find(item => item.paragraphIds?.includes(paragraph.id));
        if (!evidence_1) {
          return;
        }
        removeCitation?.(focusedField.id, {
          ...evidence_1,
          paragraphIds: [paragraph.id]
        });
        setSelectedEvidence(undefined);
      } else {
        const evidence_2 = {
          id: `pending:${paragraph.id}`,
          documentId: document.id,
          documentName: document.name,
          paragraphIds: [paragraph.id],
          origin: "USER",
          quote: paragraph.content,
          referenceLabel: "Not saved",
          pageLabel: `Page ${Array.from(new Set(paragraph.boundingRegions.map(_temp4))).join(", ")}`,
          annotations: paragraph.boundingRegions.map(region_0 => ({
            ...region_0,
            documentID: document.id,
            documentName: document.name
          }))
        };
        addCitation(focusedField, evidence_2);
        setSelectedEvidence(evidence_2);
        setActiveAnnotationIndex(0);
      }
      setAction(undefined);
    }
  } : undefined;
  const citationCount = focusedField?.evidence.length ?? 0;
  const citationIndex = focusedField?.evidence.findIndex(evidence_3 => evidence_3.id === selectedEvidence?.id || evidence_3.paragraphIds?.some(id => selectedEvidence?.paragraphIds?.includes(id))) ?? -1;
  focusedField?.evidence;
  let t7;
  if ($[13] !== focusedField?.evidence) {
    t7 = focusedField?.evidence ?? [];
    $[13] = focusedField?.evidence;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const evidence_4 = t7;
  let t8;
  if ($[15] !== evidence_4 || $[16] !== selectedEvidence) {
    t8 = selectedEvidence && !evidence_4.some(item_0 => item_0.id === selectedEvidence.id) ? [...evidence_4, selectedEvidence] : evidence_4;
    $[15] = evidence_4;
    $[16] = selectedEvidence;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const shownEvidence = t8;
  let t9;
  if ($[18] !== shownEvidence) {
    t9 = shownEvidence.flatMap(_temp5);
    $[18] = shownEvidence;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const shownAnnotations = t9;
  let t10;
  if ($[20] !== selectedEvidence?.id || $[21] !== shownEvidence) {
    let t11;
    if ($[23] !== selectedEvidence?.id) {
      t11 = evidence_6 => evidence_6.id === selectedEvidence?.id;
      $[23] = selectedEvidence?.id;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    t10 = shownEvidence.findIndex(t11);
    $[20] = selectedEvidence?.id;
    $[21] = shownEvidence;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const selectedPosition = t10;
  let t11;
  if ($[25] !== selectedPosition || $[26] !== shownEvidence) {
    t11 = shownEvidence.slice(0, Math.max(selectedPosition, 0)).reduce(_temp6, 0);
    $[25] = selectedPosition;
    $[26] = shownEvidence;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const primaryStart = t11;
  const t12 = activeAnnotationIndex === undefined || activeAnnotationIndex === NO_ACTIVE_ANNOTATION ? activeAnnotationIndex : primaryStart + activeAnnotationIndex;
  const t13 = focusedField?.label;
  const t14 = citationCount > 1 && citationIndex >= 0 ? `${citationIndex + 1} of ${citationCount}` : undefined;
  const t15 = activeAnnotationIndex === undefined ? undefined : selectedEvidence?.annotations[activeAnnotationIndex];
  const t16 = selectedEvidence?.annotations.length;
  const t17 = action === "remove";
  let t18;
  if ($[28] !== focusKeepsQuery || $[29] !== focusRequest || $[30] !== t13 || $[31] !== t14 || $[32] !== t15 || $[33] !== t16 || $[34] !== t17) {
    t18 = {
      fieldLabel: t13,
      referencePosition: t14,
      focusRequest,
      focusKeepsQuery,
      focusAnnotation: t15,
      primaryAnnotationCount: t16,
      isRemoving: t17
    };
    $[28] = focusKeepsQuery;
    $[29] = focusRequest;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = focusedFieldId ? shownAnnotations : undefined;
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setAction(undefined);
      setFocusedFieldId(undefined);
      setSelectedEvidence(undefined);
      setActiveAnnotationIndex(undefined);
    };
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== fields) {
    t21 = fieldId => {
      const evidence_8 = fields.find(item_1 => item_1.id === fieldId)?.evidence[0];
      setFocusedFieldId(fieldId);
      setAction(undefined);
      setSelectedEvidence(evidence_8);
      setSelectedDocumentId(evidence_8?.documentId);
      setActiveAnnotationIndex(evidence_8 ? 0 : undefined);
    };
    $[37] = fields;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = documentId => {
      setSelectedDocumentId(documentId);
      setSelectedEvidence(undefined);
      setActiveAnnotationIndex(undefined);
    };
    t23 = (fieldId_0, evidence_9) => {
      setFocusedFieldId(fieldId_0);
      setAction(_temp7);
      setSelectedDocumentId(evidence_9.documentId);
      setSelectedEvidence(evidence_9);
      setActiveAnnotationIndex(0);
      setFocusKeepsQuery(true);
      setFocusRequest(_temp8);
    };
    $[39] = t22;
    $[40] = t23;
  } else {
    t22 = $[39];
    t23 = $[40];
  }
  let t24;
  if ($[41] !== activeDocumentId || $[42] !== fields || $[43] !== selectedEvidence) {
    t24 = (fieldId_1, t25) => {
      const nextAction = t25 === undefined ? "add" : t25;
      const field_0 = fields.find(item_2 => item_2.id === fieldId_1);
      beforeAction.current = {
        evidence: selectedEvidence,
        documentId: activeDocumentId
      };
      setFocusedFieldId(fieldId_1);
      setAction(nextAction);
      if (nextAction === "add") {
        setSelectedEvidence(undefined);
        setActiveAnnotationIndex(NO_ACTIVE_ANNOTATION);
        return;
      }
      const evidence_10 = field_0?.evidence.find(item_3 => item_3.paragraphIds?.some(id_0 => selectedEvidence?.paragraphIds?.includes(id_0))) ?? field_0?.evidence[0];
      setSelectedEvidence(evidence_10);
      setSelectedDocumentId(evidence_10?.documentId);
      setActiveAnnotationIndex(evidence_10 ? 0 : undefined);
      setFocusKeepsQuery(false);
      setFocusRequest(_temp9);
    };
    $[41] = activeDocumentId;
    $[42] = fields;
    $[43] = selectedEvidence;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== action || $[46] !== activeDocumentId || $[47] !== cancel || $[48] !== focusedField || $[49] !== picker || $[50] !== selectedEvidence || $[51] !== t12 || $[52] !== t18 || $[53] !== t19 || $[54] !== t21 || $[55] !== t24) {
    t25 = {
      activeDocumentId,
      selectedEvidence,
      focusedField,
      action,
      picker,
      activeAnnotationIndex: t12,
      findCitation: t18,
      annotations: t19,
      close: t20,
      cancel,
      focusField: t21,
      selectDocument: t22,
      selectEvidence: t23,
      startPicking: t24
    };
    $[45] = action;
    $[46] = activeDocumentId;
    $[47] = cancel;
    $[48] = focusedField;
    $[49] = picker;
    $[50] = selectedEvidence;
    $[51] = t12;
    $[52] = t18;
    $[53] = t19;
    $[54] = t21;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  return t25;
}
function _temp9(count_1) {
  return count_1 + 1;
}
function _temp8(count_0) {
  return count_0 + 1;
}
function _temp7(current) {
  return current === "remove" ? current : undefined;
}
function _temp6(count, evidence_7) {
  return count + evidence_7.annotations.length;
}
function _temp5(evidence_5) {
  return evidence_5.annotations;
}
function _temp4(region) {
  return region.PageNumber;
}
function _temp3(evidence_0) {
  return evidence_0.paragraphIds ?? [];
}
function _temp2(evidence) {
  return evidence.paragraphIds ?? [];
}
function _temp(section) {
  return section.fields;
}